import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import CollectionCard from "@/components/CollectionCard";
import { Plus, Upload, Library } from "lucide-react";
import { toast } from "sonner";

export default function Collections() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/collections");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      let cover_path = null;
      if (coverFile) {
        const form = new FormData();
        form.append("file", coverFile);
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        cover_path = data.path;
      }
      await api.post("/collections", { name, description: desc, cover_path });
      toast.success("Collection created");
      setOpen(false); setName(""); setDesc(""); setCoverFile(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const canCreate = user && user.role === "teacher";

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
            <Library size={12} className="text-cyan8r" /> Curated drops
          </div>
          <h1 className="font-display text-5xl tracking-tighter mt-2">Collections</h1>
          <p className="text-zinc-400 mt-2 max-w-xl">Curated drops by teachers and schools — themed showcases of student work.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-collection-btn" className="bg-white text-black hover:bg-zinc-200"><Plus size={14} className="mr-1" /> New Collection</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F0F0F] border-white/10 text-white">
              <DialogHeader><DialogTitle>New Collection</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input data-testid="collection-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" /></div>
                <div><Label>Description</Label><Textarea value={desc} onChange={(e) => setDesc(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" rows={3} /></div>
                <div>
                  <Label>Cover image (optional)</Label>
                  <label className="mt-1 flex items-center gap-3 border border-dashed border-white/10 hover:border-cyan8r/40 rounded-md px-4 py-3 cursor-pointer bg-[#0A0A0A]">
                    <Upload size={14} className="text-zinc-400" />
                    <span className="text-sm text-zinc-300">{coverFile ? coverFile.name : "Choose image"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setCoverFile(e.target.files?.[0] || null)} />
                  </label>
                </div>
                <Button data-testid="collection-submit-btn" onClick={create} disabled={busy || !name} className="w-full bg-white text-black hover:bg-zinc-200">
                  {busy ? "Creating…" : "Create"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mt-10">
        {items.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-xl py-20 text-center text-zinc-500">
            No collections yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map((c) => <CollectionCard key={c.id} collection={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}
